import { createLoader } from '@meteor/data-hooks';
import { useState } from 'react';
import { withAuth } from '../../user';
import { developerClient } from '../client';

export let applicationsLoader = createLoader({
  name: 'applications',
  fetch: (i: { instanceId: string }) => withAuth(() => developerClient.application.list(i)),
  mutators: {
    create: (
      i: {
        name: string;
        description?: string;
      },
      { input: { instanceId } }
    ) =>
      withAuth(() =>
        developerClient.application.create({
          ...i,
          instanceId
        })
      ),

    update: (
      i: {
        applicationId: string;
        name?: string;
        description?: string;
      },
      { input: { instanceId } }
    ) =>
      withAuth(() =>
        developerClient.application.update({
          ...i,
          instanceId
        })
      ),

    delete: ({ applicationId }: { applicationId: string }, { input: { instanceId } }) =>
      withAuth(() =>
        developerClient.application.delete({
          instanceId,
          applicationId
        })
      ),

    createApiKey: (
      i: {
        applicationId: string;
        name: string;
        description?: string;
        expiresAt?: Date;
        type: 'publishable' | 'secret';
      },
      { input: { instanceId } }
    ) =>
      withAuth(() =>
        developerClient.apiKey.create({
          ...i,
          instanceId
        })
      ),

    updateApiKey: (
      i: {
        apiKeyId: string;
        name?: string;
        expiresAt?: Date;
        description?: string;
      },
      { input: { instanceId } }
    ) =>
      withAuth(() =>
        developerClient.apiKey.update({
          ...i,
          instanceId
        })
      ),

    deleteApiKey: ({ apiKeyId }: { apiKeyId: string }, { input: { instanceId } }) =>
      withAuth(() =>
        developerClient.apiKey.delete({
          instanceId,
          apiKeyId
        })
      ),

    rollApiKey: (
      i: {
        apiKeyId: string;
        remainingSecondsLeft: number;
      },
      { input: { instanceId } }
    ) =>
      withAuth(() =>
        developerClient.apiKey.roll({
          ...i,
          instanceId
        })
      )
  }
});

export let useApplications = (instanceId: string | null | undefined) => {
  let applications = applicationsLoader.use(instanceId ? { instanceId } : null);

  return {
    ...applications,
    createMutator: applications.useMutator('create'),
    updateMutator: applications.useMutator('update'),
    deleteMutator: applications.useMutator('delete'),
    createApiKeyMutator: applications.useMutator('createApiKey'),
    updateApiKeyMutator: applications.useMutator('updateApiKey'),
    deleteApiKeyMutator: applications.useMutator('deleteApiKey'),
    rollApiKeyMutator: applications.useMutator('rollApiKey')
  };
};

export let apiKeyLoader = createLoader({
  name: 'apiKey',
  fetch: (i: { instanceId: string; apiKeyId: string }) =>
    withAuth(() => developerClient.apiKey.get(i)),
  mutators: {
    reveal: (_, { input: { instanceId, apiKeyId } }) =>
      withAuth(() =>
        developerClient.apiKey.reveal({
          instanceId,
          apiKeyId
        })
      )
  }
});

export let useApiKey = (
  instanceId: string | null | undefined,
  apiKeyId: string | null | undefined
) => {
  let apiKey = apiKeyLoader.use(instanceId && apiKeyId ? { instanceId, apiKeyId } : null);

  return {
    ...apiKey,
    revealMutator: apiKey.useMutator('reveal')
  };
};

let revealedApiKey: Record<
  string,
  {
    secret: string;
    ts: number;
  }
> = {};
let getCachedRevealedApiKey = (apiKeyId: string) => {
  let cached = revealedApiKey[apiKeyId];
  if (!cached) return;
  if (Date.now() - cached.ts > 1000 * 60 * 30) {
    delete revealedApiKey[apiKeyId];
    return;
  }
  return cached;
};
export let useRevealableApiKey = ({
  instanceId,
  apiKeyId
}: {
  apiKeyId: string;
  instanceId: string;
}) => {
  let [value, setValue] = useState<string | undefined>(
    () => getCachedRevealedApiKey(apiKeyId)?.secret
  );
  let apiKey = useApiKey(instanceId, apiKeyId);
  let revealMutator = apiKey.revealMutator();

  let reveal = () => {
    let cached = getCachedRevealedApiKey(apiKeyId);
    if (cached) {
      setValue(cached.secret);
      return;
    }

    revealMutator.mutate({}).then(async ([res]) => {
      if (!res) return;
      revealedApiKey[apiKeyId] = {
        secret: res.secret,
        ts: Date.now()
      };
      setValue(res?.secret);
    });
  };

  let hide = () => {
    setValue(undefined);
  };

  return {
    value,
    isLoading: revealMutator.isLoading,

    reveal,
    hide
  };
};

export let revealSecret = async (opts: { apiKeyId: string; instanceId: string }) => {
  try {
    let res = await developerClient.apiKey.reveal({
      instanceId: opts.instanceId,
      apiKeyId: opts.apiKeyId
    });
    return res.secret;
  } catch (e) {
    return null;
  }
};
